import { useState } from "react";
import { useI18N } from "common/hooks";

import type { LessonStep } from "./useLessonSteps";

/**
 * Figma's first step artwork, 309x216 — only what holds the box open until
 * the image reports its own shape. CMS rows are not all the same height.
 */
const DEFAULT_ARTWORK_RATIO = 309 / 216;

/** Three steps' worth of placeholder: enough to read as "a list is coming". */
const SKELETON_STEPS = 3;

const StepArtwork = ({ step }: { step: LessonStep }) => {
  const [ratio, setRatio] = useState(DEFAULT_ARTWORK_RATIO);

  return (
    <div
      className="w-full overflow-hidden rounded-lg bg-container-neutral-minimal"
      style={{ aspectRatio: ratio }}
    >
      <img
        src={step.imageUrl}
        // The picture is the lesson; with no caption from the CMS it is left
        // out of the accessibility tree rather than announced unlabelled.
        alt={step.altText ?? ""}
        loading="lazy"
        className="h-full w-full object-cover"
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          if (naturalWidth && naturalHeight) {
            setRatio(naturalWidth / naturalHeight);
          }
        }}
      />
    </div>
  );
};

const LessonSkeleton = ({ label }: { label: string }) => (
  <div role="status" aria-label={label} aria-busy="true">
    {Array.from({ length: SKELETON_STEPS }).map((_, index) => (
      <div key={index} className="flex gap-2.5 pb-4" aria-hidden="true">
        <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-container-neutral-minimal" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-5 w-3/5 animate-pulse rounded bg-container-neutral-minimal" />
          <div className="h-4 w-full animate-pulse rounded bg-container-neutral-minimal" />
          <div
            className="w-full animate-pulse rounded-lg bg-container-neutral-minimal"
            style={{ aspectRatio: DEFAULT_ARTWORK_RATIO }}
          />
        </div>
      </div>
    ))}
  </div>
);

/**
 * A CMS album drawn as a numbered walkthrough (Figma `15662:305664`): the
 * number and its rail on the left, headline, body and artwork on the right.
 * An ordered list, so the step count is announced without the rail.
 */
const LessonStepList = ({
  steps,
  isLoading,
  loadingLabel,
  unavailableLabel,
}: {
  steps: LessonStep[];
  isLoading: boolean;
  loadingLabel: string;
  unavailableLabel: string;
}) => {
  const i18n = useI18N();

  if (isLoading) return <LessonSkeleton label={loadingLabel} />;

  if (steps.length === 0) {
    return <p className="label-s-regular text-fg-neutral-subtle">{unavailableLabel}</p>;
  }

  return (
    <ol className="flex flex-col">
      {steps.map((step, index) => (
        <li key={step.id} className="flex gap-2.5">
          {/* The rail: decorative, the list already numbers the step. */}
          <div
            aria-hidden="true"
            className="flex shrink-0 flex-col items-center gap-2 pb-2"
          >
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-container-neutral-subtle px-1 label-s-medium text-fg-neutral-on">
              {index + 1}
            </span>
            <span className="w-px flex-1 bg-border-token-brand-minimal" />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2 pb-4">
            <div className="px-1">
              <span className="sr-only">{`${i18n.get("learningLessonStep")} ${index + 1}. `}</span>
              {step.title ? (
                <p className="body-l-semibold text-fg-neutral-strong">{step.title}</p>
              ) : null}
              {step.body ? (
                <p className="label-s-regular text-fg-neutral-subtle">{step.body}</p>
              ) : null}
            </div>
            <StepArtwork step={step} />
          </div>
        </li>
      ))}
    </ol>
  );
};

export default LessonStepList;
